import React, { useEffect } from "react";
import { GeneratedDataItem } from "./types";

// Define the necessary props for this component
interface TextComponentProps {
  item: GeneratedDataItem;
  editMode: boolean;
  editData: Record<string, any>;
  handleInputChange: (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => void;
}

// Helper function to format text with bullet points
const formatTextWithBullets = (text: string) => {
  const lines = text.split("\n");
  const result: React.ReactElement[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    if (!trimmed) {
      result.push(<br key={`br-${i}`} />);
      return;
    }

    // Handle numbered lists (1., 2., etc.)
    if (line.match(/^\d+\. /)) {
      const content = line.replace(/^\d+\. /, "");
      result.push(
        <div key={`ol-${i}`} className="flex ml-5 my-1">
          <span className="mr-2 text-blue-600 font-bold">•</span>
          <span>{content}</span>
        </div>
      );
    }
    // Handle bullet points (- )
    else if (line.startsWith("- ")) {
      result.push(
        <div key={`ul-${i}`} className="flex ml-5 my-1">
          <span className="mr-2 text-blue-600">•</span>
          <span>{line.substring(2)}</span>
        </div>
      );
    }
    // Handle bullet points with • symbol
    else if (line.startsWith("• ")) {
      result.push(
        <div key={`bullet-${i}`} className="flex ml-5 my-1">
          <span className="mr-2 text-blue-600">•</span>
          <span>{line.substring(2)}</span>
        </div>
      );
    }
    // Handle regular text
    else {
      result.push(
        <div key={`text-${i}`} className="my-1">
          {trimmed}
        </div>
      );
    }
  });

  return result;
};

const TextComponent: React.FC<TextComponentProps> = ({
  item,
  editMode,
  editData,
  handleInputChange,
}) => {
  // Debug: Log when item data changes
  useEffect(() => {
    console.log("TextComponent item updated:", {
      id: item._id,
      text: item.text,
      editMode,
      editData,
      editDataText: editData?.text,
      textareaValue: editData?.text?.text || (typeof item.text === "object" ? item.text.text : item.text) || ""
    });
  }, [item, editMode, editData]);

  if (!item.text && item.component_type !== "Text") return null;

  if (editMode) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title:
          </label>
          <input
            type="text"
            value={
              editData?.text?.title ||
              (typeof item.text === "object" ? item.text.title : "") ||
              "Text"
            }
            onChange={(e) =>
              handleInputChange(
                item._id || "",
                "text.title",
                e.target.value,
                "text"
              )
            }
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content:
          </label>
          <textarea
            value={
              editData?.text?.text ||
              (typeof item.text === "object" ? item.text.text : item.text) ||
              ""
            }
            onChange={(e) =>
              handleInputChange(
                item._id || "",
                "text.text",
                e.target.value,
                "text"
              )
            }
            className="w-full p-2 border border-gray-300 rounded min-h-[200px]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Citation:
          </label>
          <input
            type="text"
            value={
              editData?.text?.citation ||
              (typeof item.text === "object" ? item.text.citation : "") ||
              ""
            }
            onChange={(e) =>
              handleInputChange(
                item._id || "",
                "text.citation",
                e.target.value,
                "text"
              )
            }
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
      </div>
    );
  }

  // Render text for viewing
  const textContent =
    typeof item.text === "object" ? item.text.text : item.text || "";

  return (
    <>
      <h4 className="text-lg font-bold mb-2">
        {typeof item.text === "object" ? item.text.title : ""}
      </h4>
      <div className="whitespace-pre-line">
        {formatTextWithBullets(textContent)}
      </div>
      {typeof item.text === "object" && item.text.citation && (
        <p className="text-sm text-gray-500 mt-2">
          Source: {item.text.citation}
        </p>
      )}
    </>
  );
};

export default TextComponent;
